// External libraries
import * as Sentry from '@sentry/react';

// React
import { useEffect, useState } from 'react';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';

// Helpers
import { isEmptyCustom } from '../../helpers/utils';

// Components
import MainContent from './MainContent';
import ForgotPasswordPage from '../auth/ForgotPassword/ForgotPasswordPage';
import LoginPage from '../auth/Login/LoginPage';
import ResetPasswordPage from '../auth/ResetPassword/ResetPasswordPage';
import NotificationsManager from '../../components/notifications/NotificationsManager';
import ProtectedRoute from './ProtectedRoute';
import SsoReauth from './SsoReauth';
import Logout from './Logout';

// Actions
import useTokenValidator from './useTokenValidator';

// Store
import { useAppSelector } from '../../store';
import { useDispatch } from 'react-redux';

// Reducers and actions
import { clearPublisher } from '../Browse/publisherReducer';
import { clearPublishers } from '../Browse/publishersReducer';
import { clearNotifications } from '../../components/notifications/notificationsReducer';
import { logout } from '../auth/authReducer';

// Helpers
import { getAuthMode, setAuthMode, clearReauthMarker } from '../../helpers/auth';

const EXTERNAL_ROUTES = ['/login', '/logout', '/external-logout', '/auth', '/forgot'];

const AppRoutes = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const path = location.pathname;
    const dispatch = useDispatch();

    const [checkIfInternalUser, setCheckIfInternalUser] = useState(false);

    const internalUserJwt = new URLSearchParams(location.search).get('jwt');
    const isExternalRoute = EXTERNAL_ROUTES.includes(path);

    if (internalUserJwt && !checkIfInternalUser) {
        dispatch(clearPublisher());
        dispatch(clearPublishers());
        dispatch(clearNotifications());
        dispatch(logout());
        // Arriving with a jwt query param is the SSO round trip from
        // advertising-apps-auth. Remember that so a later tab with no persisted user
        // (e.g. opened via cmd+click) knows to re-run SSO instead of showing the
        // external password form.
        setAuthMode('sso');
        // A token just arrived, so this tab is no longer in the "already tried and
        // failed" state; let a later expiry in this same tab re-auth again.
        clearReauthMarker();
        setCheckIfInternalUser(true);
    }

    const publisher = useAppSelector(state => state.publisher);

    const { verifyToken } = useTokenValidator();
    const isAuthenticated = verifyToken();

    useEffect(() => {
        if (isExternalRoute) {
            return;
        }

        if (isAuthenticated) {
            // Only send the user to the publisher picker when we cannot resolve a
            // publisher for the requested route at all. A deep link into a
            // publisher-scoped page (e.g. /zones/edit/:zoneId/overview) should be
            // honoured once authenticated, not overridden here just because this is a
            // fresh tab or an SSO round trip. The old `!isEmptyCustom(internalUserJwt)`
            // clause forced every SSO arrival through /browse regardless of the deep
            // link it was carrying; that clause is removed.
            if (isEmptyCustom(publisher)) {
                const isRealDeepLink = path !== '/' && path !== '/browse';

                if (isRealDeepLink) {
                    // We cannot resolve a publisher for this route without one already
                    // in state, so route through the picker, but carry the requested
                    // destination along as router state. Browse.tsx sends the user on
                    // to it once a publisher is actually resolved (auto-selected for a
                    // single-publisher user, or chosen for a multi-publisher one).
                    // Strip the jwt param first: a credential must not ride along in
                    // router state any more than it should ride in a URL (see
                    // currentPathWithoutJwt in helpers/auth.ts for the same reasoning).
                    const params = new URLSearchParams(location.search);
                    params.delete('jwt');
                    const search = params.toString();

                    navigate('/browse', {
                        state: {
                            from: {
                                pathname: path,
                                search: search ? `?${search}` : '',
                                hash: location.hash,
                            },
                        },
                    });
                } else {
                    navigate('/browse');
                }
            } else if (path === '/') {
                navigate('/home/reporting');
            }
        }
    }, []);

    /**
     * - By default a route is considered as exact
     * - They can be nested
     <Route path="/the-path" element={<Element />}>
     <Route path="/sub-path" element={<Element />} /> //the-path/sub-path
     </Route>
     OR use * to signify non-exact path
     * - component={Component} => element={<Element />}
     */
    return (
        <>
            <Routes>
                <Route path="login" element={<LoginPage />} />
                <Route path="forgot" element={<ForgotPasswordPage />} />
                <Route path="auth" element={<ResetPasswordPage />} />
                <Route path="logout" element={<Logout />} />
                <Route path="external-logout" element={<Logout external />} />
                {/* Protected routes */}
                <Route
                    path="*"
                    element={
                        !isAuthenticated && getAuthMode() === 'sso' ? (
                            // Internal user with no usable token in this tab (fresh tab /
                            // new-tab deep link, or an expiry). Re-run SSO instead of
                            // showing the external password form, which internal users
                            // have no credentials for.
                            <SsoReauth />
                        ) : (
                            <ProtectedRoute
                                element={<MainContent />}
                                redirectPath={'/login'}
                                isAuthenticated={isAuthenticated}
                            />
                        )
                    }
                />
            </Routes>
            <NotificationsManager isLoggedIn={isAuthenticated} />
        </>
    );
};

export default Sentry.withProfiler(AppRoutes);
